/**
 * DayNight — one clock, and the light that follows from it.
 *
 * Every shader in this game already takes the same handful of things: a direction toward the
 * key light, its colour, a two-tone ambient, a fog colour and a density. They were constants
 * read once at construction. This makes them a function of time and pushes them out each
 * frame, which is the whole cycle — there is no second lighting path for night, because night
 * is just a point on the same curve.
 *
 * THE SUN SETS AND THE MOON TAKES OVER, and the handover is the only fiddly part. Both are
 * one key light whose direction sweeps a full circle over the day; below the horizon it is
 * flipped back up and its colour and intensity cross-fade to moonlight. That keeps every
 * shadow, every caustic and every rim light working through the night with no special case —
 * a moonlit reef is a dim blue reef with long shadows, which is exactly what it is.
 *
 * `night` is exported separately because one thing genuinely needs to know the difference:
 * bioluminescence does not fade in with the light, it is invisible until the water is dark.
 */
import * as THREE from 'three';
import { WATER } from '../data/tuning.ts';

/** real seconds for a full day. Long enough to be a setting, short enough to be seen. */
export const DAY_SECONDS = 420;
/** phase the run starts at: mid-morning, so the first thing anyone sees is lit */
export const START_PHASE = 0.34;

function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

/**
 * Keyframes around the clock. `k` is the phase, 0 and 1 both solar midnight.
 *
 * Authored as sRGB hex and converted once: these are art-direction values, and nobody picks a
 * colour in linear. The noon row is deliberately the exact WATER palette the rest of the game
 * was tuned against, so midday looks like it always did and everything else is a departure
 * from a known-good frame.
 */
interface Key {
  k: number;
  sun: number;
  ambTop: number;
  ambBottom: number;
  fog: number;
  /** multiplier on the base fog density; night water reads thicker */
  density: number;
  /** how much the surface still focuses light onto the bed */
  caustic: number;
  /** volumetric shaft brightness */
  shafts: number;
  night: number;
}

const KEYS: Key[] = [
  // deep night: moonlight only, and the water swallows most of it
  { k: 0.00, sun: 0x8fb6e0, ambTop: 0x1b3550, ambBottom: 0x060d1a, fog: 0x0a1a2e, density: 1.30, caustic: 0.16, shafts: 0.10, night: 1.00 },
  { k: 0.19, sun: 0xa8bcd8, ambTop: 0x27455f, ambBottom: 0x0b1626, fog: 0x123049, density: 1.22, caustic: 0.22, shafts: 0.18, night: 0.88 },
  // first light: low sun, so it is red and it rakes
  { k: 0.26, sun: 0xff9c5e, ambTop: 0x5c6f82, ambBottom: 0x1d2c3c, fog: 0x315a6e, density: 1.06, caustic: 0.42, shafts: 0.44, night: 0.42 },
  { k: 0.33, sun: 0xffd7a8, ambTop: 0x7fb0c0, ambBottom: 0x24404e, fog: 0x3d7f8c, density: 0.98, caustic: 0.72, shafts: 0.72, night: 0.14 },
  // noon: the values the whole game was tuned against
  { k: 0.50, sun: 0xfff0d6, ambTop: 0x7fb6c2, ambBottom: 0x1b4450, fog: 0x4a97a0, density: 1.00, caustic: 1.00, shafts: 1.00, night: 0.00 },
  { k: 0.67, sun: 0xffd9a0, ambTop: 0x76a8ba, ambBottom: 0x214050, fog: 0x3f8290, density: 1.00, caustic: 0.80, shafts: 0.80, night: 0.10 },
  // sunset, the mirror of dawn but warmer and slower
  { k: 0.76, sun: 0xff8a4c, ambTop: 0x62707f, ambBottom: 0x1a2a38, fog: 0x2f5468, density: 1.08, caustic: 0.38, shafts: 0.40, night: 0.48 },
  { k: 0.84, sun: 0x9db4d4, ambTop: 0x2b4660, ambBottom: 0x0d1826, fog: 0x143352, density: 1.20, caustic: 0.20, shafts: 0.20, night: 0.86 },
  // wraps to the first
  { k: 1.00, sun: 0x8fb6e0, ambTop: 0x1b3550, ambBottom: 0x060d1a, fog: 0x0a1a2e, density: 1.30, caustic: 0.16, shafts: 0.10, night: 1.00 },
];

export interface SkyEnv {
  /** unit vector pointing TOWARD the key light, always in the upper hemisphere */
  sunDir: THREE.Vector3;
  sunColor: THREE.Color;
  ambientTop: THREE.Color;
  ambientBottom: THREE.Color;
  fogColor: THREE.Color;
  fogDensity: number;
  caustic: number;
  shafts: number;
  /** 0 broad daylight, 1 fully dark */
  night: number;
  /** 0..1 around the clock, 0 = solar midnight */
  phase: number;
}

const A = { sun: srgb(0), aT: srgb(0), aB: srgb(0), fog: srgb(0) };
const B = { sun: srgb(0), aT: srgb(0), aB: srgb(0), fog: srgb(0) };

export class DayNight {
  readonly env: SkyEnv = {
    sunDir: new THREE.Vector3(0, 1, 0),
    sunColor: srgb(0xffffff),
    ambientTop: srgb(0x000000),
    ambientBottom: srgb(0x000000),
    fogColor: srgb(0x000000),
    fogDensity: WATER.fogDensity,
    caustic: 1,
    shafts: 1,
    night: 0,
    phase: START_PHASE,
  };

  private t = START_PHASE * DAY_SECONDS;
  /** wall-clock seconds per simulated second; 0 freezes the sky where it is */
  rate = 1;
  private anchored = false;

  /**
   * Tie the clock to absolute time, so everyone in a bay shares a sky.
   *
   * Two clients running their own clock from their own start time are simply in different days,
   * and no amount of matching rate will bring them together: one is at dawn while the other is
   * at dusk, they cast shadows in opposite directions, and the bioluminescence is on for one of
   * them and not the other. Deriving the phase from the epoch instead makes it a pure function
   * of a number both already agree on, with nothing to send and nothing to synchronise.
   *
   * Date.now() and not the relay's clock, deliberately: the relay counts from its own start, so
   * a redeploy would jump the sky, and this must keep working for a client whose socket is down.
   * At 420 seconds a day, a second of NTP skew is a quarter of one per cent of the cycle.
   *
   * Recomputed every frame rather than integrated. Integration accumulates float error and
   * drifts apart over a long session, which is the exact failure this exists to prevent.
   */
  syncToWallClock(): void {
    this.anchored = true;
  }

  advance(dt: number): SkyEnv {
    if (this.anchored) {
      // `rate` is ignored here on purpose: scaling absolute time would put every client on a
      // different curve again the moment one of them changed it.
      this.t = ((Date.now() / 1000) % DAY_SECONDS + DAY_SECONDS) % DAY_SECONDS;
      return this.sample(this.t / DAY_SECONDS);
    }
    this.t = (this.t + dt * this.rate) % DAY_SECONDS;
    if (this.t < 0) this.t += DAY_SECONDS;
    return this.sample(this.t / DAY_SECONDS);
  }

  /** jump the clock; `p` is 0..1 around the day */
  setPhase(p: number): void {
    this.t = ((p % 1) + 1) % 1 * DAY_SECONDS;
  }

  sample(phase: number): SkyEnv {
    const e = this.env;
    e.phase = phase;

    let i = 0;
    while (i < KEYS.length - 2 && KEYS[i + 1].k < phase) i++;
    const a = KEYS[i];
    const b = KEYS[i + 1];
    const span = b.k - a.k;
    const raw = span > 1e-6 ? (phase - a.k) / span : 0;
    const f = raw <= 0 ? 0 : raw >= 1 ? 1 : raw * raw * (3 - 2 * raw);

    A.sun.setHex(a.sun, THREE.SRGBColorSpace); B.sun.setHex(b.sun, THREE.SRGBColorSpace);
    A.aT.setHex(a.ambTop, THREE.SRGBColorSpace); B.aT.setHex(b.ambTop, THREE.SRGBColorSpace);
    A.aB.setHex(a.ambBottom, THREE.SRGBColorSpace); B.aB.setHex(b.ambBottom, THREE.SRGBColorSpace);
    A.fog.setHex(a.fog, THREE.SRGBColorSpace); B.fog.setHex(b.fog, THREE.SRGBColorSpace);

    e.sunColor.copy(A.sun).lerp(B.sun, f);
    e.ambientTop.copy(A.aT).lerp(B.aT, f);
    e.ambientBottom.copy(A.aB).lerp(B.aB, f);
    e.fogColor.copy(A.fog).lerp(B.fog, f);
    e.fogDensity = WATER.fogDensity * (a.density + (b.density - a.density) * f);
    e.caustic = a.caustic + (b.caustic - a.caustic) * f;
    e.shafts = a.shafts + (b.shafts - a.shafts) * f;
    e.night = a.night + (b.night - a.night) * f;

    /* The key light sweeps a full circle: up over the day, down over the night, and flipped
       back into the upper hemisphere when it is below. The flip is what makes a moon out of a
       sun — same maths, same shadows, opposite side of the sky. The azimuth drifts too, so
       shadows swing round rather than just shortening and lengthening. */
    const ang = (phase - 0.25) * Math.PI * 2;
    const elev = Math.sin(ang);
    const az = phase * Math.PI * 2 + 0.7;
    const horiz = Math.cos(ang);
    e.sunDir.set(Math.cos(az) * horiz, elev, Math.sin(az) * horiz);
    if (e.sunDir.y < 0) e.sunDir.negate();
    // Never let it sit exactly on the horizon: a key light in the plane of a flat seabed
    // lights nothing at all and every normal goes to the ambient term.
    if (e.sunDir.y < 0.16) {
      e.sunDir.y = 0.16;
      e.sunDir.normalize();
    }
    return e;
  }

  /** for the debug overlay */
  clockString(): string {
    // phase 0 IS solar midnight, so it maps straight onto the hour with no offset
    const h = (this.env.phase * 24) % 24;
    const hh = Math.floor(h);
    const mm = Math.floor((h - hh) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
}

/**
 * Push the environment into any material that speaks the common uniform names.
 *
 * Structural rather than an interface each material implements: there are nine shaders here
 * that all grew the same five uniforms independently, some as Color and some as Vector3, and
 * under two different names for the light direction. Matching on what a material actually has
 * costs one function and means a new shader joins the day cycle by naming its uniforms
 * conventionally and doing nothing else.
 */
export function applyEnv(u: Record<string, THREE.IUniform> | undefined, env: SkyEnv): void {
  if (!u) return;
  putVec(u.uSunDir, env.sunDir);
  putVec(u.uLightDir, env.sunDir);
  putCol(u.uSunColor, env.sunColor);
  putCol(u.uAmbientTop, env.ambientTop);
  putCol(u.uAmbTop, env.ambientTop);
  putCol(u.uAmbientBottom, env.ambientBottom);
  putCol(u.uAmbBottom, env.ambientBottom);
  putCol(u.uFogColor, env.fogColor);
  if (u.uFogDensity) u.uFogDensity.value = env.fogDensity;
  if (u.uNight) u.uNight.value = env.night;
}

/** the direction uniforms are Vector3 everywhere; the colour ones are not */
function putVec(slot: THREE.IUniform | undefined, v: THREE.Vector3): void {
  if (!slot || !slot.value) return;
  const t = slot.value as THREE.Vector3;
  if ((t as unknown as { isVector3?: boolean }).isVector3) t.copy(v);
}

function putCol(slot: THREE.IUniform | undefined, c: THREE.Color): void {
  if (!slot || !slot.value) return;
  const t = slot.value as THREE.Color & THREE.Vector3;
  if ((t as unknown as { isColor?: boolean }).isColor) (t as THREE.Color).copy(c);
  else if ((t as unknown as { isVector3?: boolean }).isVector3) (t as THREE.Vector3).set(c.r, c.g, c.b);
}
